import { Box, Button, TextField } from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import React, { useCallback, useEffect, useRef } from "react";
import WatsonApi from "../../../backends/WatsonApi";
import { MessagingListQueryOptions } from "./MessagingListRules";
import { IMessageListRuleTag } from "./MessagingListRuleQueryBuilder";
import { useStores } from "../../../stores/useStores";
import { IMessagingListModalIntent } from "./AddOrEditMessagingListModal";
import { getAllowedOrganizationTypes } from "./ruleOrganizationTypes";

export function RuleOrganizationSearch(props: {
  setOrganizations: React.Dispatch<React.SetStateAction<IMessageListRuleTag[]>>;
  modalIntent: IMessagingListModalIntent;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  setSearchResultsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  searchResultsLoading: boolean;
  resetSearch: () => void;
}) {
  const searchTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { organizationStore } = useStores();

  useEffect(() => {
    return () => {
      if (searchTimeOutRef.current) clearTimeout(searchTimeOutRef.current);
    };
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      if (searchTimeOutRef.current) clearTimeout(searchTimeOutRef.current);
      props.setSearchValue(e.target.value);

      if (!e.target.value) return;
      props.setSearchResultsLoading(true);
      searchTimeOutRef.current = setTimeout(async () => {
        try {
          const client = await WatsonApi();
          const response = await client.apis.organizations.organizations_list({
            type__in: getAllowedOrganizationTypes(
              organizationStore.currentOrganization.type,
            ),
            fields: ["title", "type", "id"],
            search: e.target.value,
            deleted: "false",
            ordering: "title",
            page_size: 12,
            path__descendant_or_eq: organizationStore.currentOrganization.path,
            with_role: props.modalIntent === "createMessage" ? true : undefined,
          });
          const results = JSON.parse(response.data).data;
          const options = results.map((org) => ({
            value: `${org.title} (${org.type})`,
            type: MessagingListQueryOptions.Organization,
            filter: org.id,
          }));
          props.setOrganizations(options);
        } catch (error) {
          console.error("Error fetching organizations:", error);
        } finally {
          props.setSearchResultsLoading(false);
        }
      }, 250);
    },
    [organizationStore.currentOrganization.path],
  );

  return (
    <Box
      pb={"1"}
      // Radix Select.Content's typeahead consumes keydown, and its focus
      // management grabs pointerdown — both fight the search input. Wrapping
      // here (rather than on TextField.Root) also covers clicks on the slot
      // buttons.
      onKeyDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <TextField.Root
        ref={inputRef}
        onChange={handleInputChange}
        placeholder={"Search"}
        value={props.searchValue}
      >
        <TextField.Slot side={"left"}>
          {props.searchResultsLoading ? (
            <FontAwesome name={"fa-solid fa-spinner"} spin />
          ) : (
            <FontAwesome name={"fa-magnifying-glass"} prefix={"fas"} />
          )}
        </TextField.Slot>
        <TextField.Slot side={"right"}>
          <Button
            variant={"ghost"}
            size={"1"}
            color={"gray"}
            onClick={() => {
              props.resetSearch();
              inputRef.current?.focus();
            }}
          >
            <FontAwesome prefix={"fas"} name={"fa-times"} />
          </Button>
        </TextField.Slot>
      </TextField.Root>
    </Box>
  );
}
